/**
 * This file provides a context for managing the state of the swimlane component.
 * It's used mostly for virtualization purposes like card, cell, and row heights calculation.
 *
 * Card calculation is done in 3 steps:
 * 1. Initial setup: when the component props set, the minimal card height and calculating function are set based on the format settings. (isCompact, isItemDescriptionVisible, isItemDateVisible, etc)
 * 2. Cards height calculation: when the cards are set, the height of each card is calculated based on the card properties and the calculating function by chunks using idle callback in background.
 * 3. Cards height update: when the card height is changed (e.g. when virtualized cards displayed or by user interaction), the height is updated in the state using ResizeObserver.
 *
 * 1 is done in the CardHeightCalculationStateManager, 2 in the useCardsHeightCalculation, 3 in the SwimlaneStateProvider.
 *
 * Cell height calculation is centralized in `computeCellHeight`, a pure helper that accounts for cards, drafts,
 * and the `+ Add item` footer (when visible). It is the single source of truth shared by the initial-render pass
 * in `SwimlaneStateProvider` and the runtime updates in `resetStateCellHeight`. `readOnly` and `maxItemsPerCell`
 * are mirrored from `useSwimlaneRootProps` onto state so this helper can gate footer height on the same condition
 * as the JSX render gate in `Cards.tsx`, keeping virtualization and layout in lockstep.
 *
 * Row heights calculation is done in the SwimlaneStateProvider on corresponding card height changes, and uses
 * cellHeight (plus wrapper padding) as its input, taking the max across cells in the row.
 *
 * The context provides the following methods:
 * getCellCardIDs, hasCardDraft, removeCardDraft, getCardHeight are used in `Cards` to display virtualized cards.
 * getCardProps is used in `CardRenderer` to display card in cells, DraggingCardsPreview, etc.
 * getCellHeight is used in `SwimlaneGridCell` to set the inner cell wrapper's minHeight.
 * getRowHeight is used in `SwimlaneGridCell` to set the cell height and in rows virtualization (VirtualizerRoot).
 * addCardDraft is used in `InlineAdd` to add draft card to the cell.
 * onCardMount, onCardUnmount are used in `Card` to register and unregister card element in ResizeObserver.
 */

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useSwimlaneEventPublisher } from '../../api';
import {
  ADD_ITEM_FOOTER_HEIGHT,
  CELL_GAP,
  CELL_WRAPPER_PADDING,
  CELL_WRAPPER_PADDING_BOTTOM,
  DRAFT_CARD_HEIGHT,
  ROW_HEIGHT_MIN,
} from '../../layout/constants';
import type {
  ICard,
  TCardDraftId,
  TCardId,
  TCellId,
  TColumnID,
  TItem,
  TItemID,
  TRowID,
} from '../../types';
import { buildCardId, buildCellId, parseCardId, parseCellId, parseDraftId } from '../../utils';
import { useSwimlaneRootProps } from '../SwimlaneRootPropsContext';
import {
  CardHeightCalculationStateManager,
  hasCardSizePropsChanged,
  minCardHeight,
  type TCardHeightCalculationState,
} from './CardHeightCalculationStateManager';
import { useCardsHeightCalculation } from './useCardsHeightCalculation';

// COULDDO: consider separating props- and height-related state (need to check rerenders)
type TSwimlaneState = {
  cellCardIDs: Map<TCellId, TCardId[]>;
  cardProps: Map<TCardId, ICard>;
  cardHeight: Map<TCardId, number>;
  defaultCardHeight: number;
  cardDraft: Set<TCardDraftId>;
  cellHeight: Map<TRowID, Map<TColumnID, number>>;
  rowHeight: Map<TRowID, number>;
  unmeasuredCards: Set<TCardId>;
  cardsDraggedOver: Map<TCardId, TCardId>;
  readOnly: boolean;
  maxItemsPerCell: number;
};

const EMPTY_STATE: TSwimlaneState = {
  cellCardIDs: new Map(),
  cardProps: new Map(),
  cardHeight: new Map(),
  defaultCardHeight: minCardHeight,
  cardDraft: new Set(),
  cellHeight: new Map(),
  rowHeight: new Map(),
  unmeasuredCards: new Set(),
  cardsDraggedOver: new Map(),
  readOnly: false,
  maxItemsPerCell: Number.MAX_SAFE_INTEGER,
} as const;

const createEmptyState = (): TSwimlaneState => structuredClone(EMPTY_STATE);
const createEmptyChangedCardHeights = () => new Map<TCellId, Map<TCardId, number>>();

const getStateCardHeight = (state: TSwimlaneState, cardId: TCardId) =>
  state.cardHeight.get(cardId) ?? state.defaultCardHeight ?? minCardHeight;

const resetStateRowHeight = (state: TSwimlaneState, rowID: TRowID) => {
  const cellHeightMap = state.cellHeight.get(rowID);
  const allCellHeights = [ROW_HEIGHT_MIN.value];
  const overColumnIds = new Set<TColumnID>();
  for (const overCardId of state.cardsDraggedOver.keys()) {
    const { rowID: overRowID, columnID } = parseCardId(overCardId);
    if (overRowID === rowID) {
      overColumnIds.add(columnID);
    }
  }
  const draggingCardId = state.cardsDraggedOver.values().next().value;
  for (const columnID of cellHeightMap?.keys() ?? []) {
    const rawCellHeight = cellHeightMap?.get(columnID) ?? 0;
    if (rawCellHeight === 0) continue;
    let cellHeight = rawCellHeight + CELL_WRAPPER_PADDING.value + CELL_WRAPPER_PADDING_BOTTOM.value;
    if (draggingCardId && overColumnIds.has(columnID)) {
      cellHeight += CELL_GAP.value + getStateCardHeight(state, draggingCardId);
    }
    allCellHeights.push(cellHeight);
  }

  state.rowHeight.set(rowID, Math.max(...allCellHeights));

  return state;
};

const computeCellHeight = (state: TSwimlaneState, cellId: TCellId): number => {
  const cardIds = state.cellCardIDs.get(cellId) ?? [];

  let cellHeight = 0;
  if (state.cardDraft.has(cellId)) {
    cellHeight += DRAFT_CARD_HEIGHT.value;
  }

  for (const cardId of cardIds) {
    if (cellHeight) {
      cellHeight += CELL_GAP.value;
    }
    cellHeight += getStateCardHeight(state, cardId);
    if (state.cardDraft.has(cardId)) {
      cellHeight += CELL_GAP.value + DRAFT_CARD_HEIGHT.value;
    }
  }

  const shouldRenderAddItemFooter =
    cardIds.length > 0 && !state.readOnly && cardIds.length < state.maxItemsPerCell;
  if (shouldRenderAddItemFooter) {
    cellHeight += CELL_GAP.value + ADD_ITEM_FOOTER_HEIGHT.value;
  }

  return cellHeight;
};

const writeCellHeight = (state: TSwimlaneState, cellId: TCellId) => {
  const cardIds = state.cellCardIDs.get(cellId) ?? [];
  if (cardIds.length === 0) return;
  const { rowID, columnID } = parseCellId(cellId);
  if (!state.cellHeight.has(rowID)) {
    state.cellHeight.set(rowID, new Map());
  }
  state.cellHeight.get(rowID)?.set(columnID, computeCellHeight(state, cellId));
};

const resetStateCellHeight = (state: TSwimlaneState, cellId: TCellId) => {
  writeCellHeight(state, cellId);
  const { rowID } = parseCellId(cellId);
  state.rowHeight = new Map(state.rowHeight);
  return resetStateRowHeight(state, rowID);
};

export type TSwimlaneStateContext = {
  getCellCardIDs: (cellId: TCellId) => TCardId[];
  getCardProps: (cardId: TCardId) => ICard | undefined;
  getCardHeight: (cardId: TCardId) => number;
  getRowHeight: (rowId: TRowID) => number;
  getCellHeight: (rowID: TRowID, columnID: TColumnID) => number;
  hasCardDraft: (draftId: TCardDraftId) => boolean;
  addCardDraft: (draftId: TCardDraftId) => void;
  removeCardDraft: (draftId: TCardDraftId) => void;
  onCardMount: (cardElement: HTMLElement) => void;
  onCardUnmount: (cardElement: HTMLElement) => void;
  onCardDropIndicatorShown: (overCardId: TCardId, draggingCardId: TCardId) => void;
  onCardDropIndicatorHidden: (overCardId: TCardId) => void;
};

export const SwimlaneStateContext = createContext<TSwimlaneStateContext | null>(null);

const emptyCellCardIDs: TCardId[] = [] as const;

type TSwimlaneStateProviderProps = {
  cells: Map<TRowID, Map<TColumnID, TItemID[]>>;
  items: Map<TItemID, TItem>;
  children: ReactNode;
};

export const SwimlaneStateProvider = ({ cells, items, children }: TSwimlaneStateProviderProps) => {
  const { readOnly, maxItemsPerCell } = useSwimlaneRootProps();
  const [cardHeightCalculatorState, setCardHeightCalculatorState] =
    useState<TCardHeightCalculationState | null>(null);
  const [swimlaneState, setSwimlaneState] = useState<TSwimlaneState>(createEmptyState);
  const defaultCardHeight = cardHeightCalculatorState?.defaultCardHeight ?? minCardHeight;
  const calculateCardHeight = cardHeightCalculatorState?.calculateCardHeight;
  const calculateCardHeightRef = useRef(calculateCardHeight);
  const changedCardHeightRef = useRef(createEmptyChangedCardHeights());

  useEffect(() => {
    if (!calculateCardHeight) return;

    const isCalculateCardHeightChanged = calculateCardHeightRef.current !== calculateCardHeight;
    if (isCalculateCardHeightChanged) {
      calculateCardHeightRef.current = calculateCardHeight;
    }

    // COULDDO: optimize this:
    // e.g. by updating only the changed elements (immer?), caching the previous state, measuring cards only when needed,
    // calculating by chunks in background (idleCallback?) etc.
    setSwimlaneState((prevState) => {
      const nextState = createEmptyState();
      nextState.cardDraft = new Set(prevState.cardDraft);
      nextState.defaultCardHeight = defaultCardHeight;
      nextState.cardsDraggedOver = new Map(prevState.cardsDraggedOver);
      nextState.readOnly = readOnly;
      nextState.maxItemsPerCell = maxItemsPerCell;

      for (const [rowID, rowCells] of cells) {
        for (const [columnID, itemIDs] of rowCells) {
          const cellId = buildCellId({ rowID, columnID });
          for (const itemID of itemIDs) {
            if (!items.has(itemID)) {
              continue;
            }
            const cardProps: ICard = { rowID, columnID, itemID, ...(items.get(itemID) as TItem) };
            const cardId = buildCardId({ rowID, columnID, itemID });
            const cellCardIds = nextState.cellCardIDs.get(cellId) ?? [];

            const isCardHeightChanged =
              isCalculateCardHeightChanged ||
              hasCardSizePropsChanged(prevState.cardProps.get(cardId), cardProps);
            const prevCardHeight = prevState.cardHeight.get(cardId) ?? 0;

            cellCardIds.push(cardId);
            nextState.cellCardIDs.set(cellId, cellCardIds);
            nextState.cardProps.set(cardId, cardProps);
            if (!isCardHeightChanged && prevCardHeight) {
              nextState.cardHeight.set(cardId, prevCardHeight);
            } else {
              nextState.unmeasuredCards.add(cardId);
            }
          }
          writeCellHeight(nextState, cellId);
        }
      }

      for (const [rowID] of nextState.cellHeight) {
        resetStateRowHeight(nextState, rowID);
      }

      return nextState;
    });
  }, [calculateCardHeight, defaultCardHeight, cells, items, readOnly, maxItemsPerCell]);

  const publishEvent = useSwimlaneEventPublisher();
  // biome-ignore lint/correctness/useExhaustiveDependencies: dependency array is intentionally limited to prevent excessive re-renders
  useEffect(() => {
    publishEvent('swimlaneRowHeightChange');
  }, [publishEvent, swimlaneState.rowHeight]);

  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const onCardsHeightChange = useCallback((changes: Map<TCardId, number>, overwrite = false) => {
    if (!changes?.size) {
      return;
    }

    setSwimlaneState((prevState) => {
      const changedCards = changedCardHeightRef.current;
      for (const [cardId, cardHeight] of changes) {
        const prevHeight = prevState.cardHeight.get(cardId);
        if (cardHeight && prevHeight !== cardHeight && (!prevHeight || overwrite)) {
          const cellId = buildCellId(parseCardId(cardId));
          prevState.cardHeight.set(cardId, cardHeight);
          if (!changedCards.has(cellId)) {
            changedCards.set(cellId, new Map());
          }
          changedCards.get(cellId)?.set(cardId, cardHeight);
          prevState.unmeasuredCards.delete(cardId);
        }
      }

      if (!changedCards.size) {
        return prevState;
      }

      const nextState = {
        ...prevState,
        cardHeight: new Map(prevState.cardHeight),
        unmeasuredCards: new Set(prevState.unmeasuredCards),
      };
      for (const cellId of changedCards.keys()) {
        resetStateCellHeight(nextState, cellId);
      }

      return nextState;
    });
  }, []);

  useEffect(() => {
    resizeObserverRef.current = new ResizeObserver((entries) => {
      const changes = new Map<TCardId, number>();
      for (const { target, borderBoxSize } of entries) {
        const cardId = target.getAttribute('data-card-id') as TCardId;
        const cardHeight = borderBoxSize[0]?.blockSize;
        if (cardHeight) {
          changes.set(cardId, cardHeight);
        }
      }

      if (changes.size > 0) {
        onCardsHeightChange(changes, true);
      }
    });

    return () => {
      resizeObserverRef.current?.disconnect();
    };
  }, [onCardsHeightChange]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependency array is intentionally limited to prevent excessive re-renders
  useEffect(() => {
    if (changedCardHeightRef.current.size) {
      publishEvent('swimlaneCardHeightChange', { changedCards: changedCardHeightRef.current });
      changedCardHeightRef.current = createEmptyChangedCardHeights();
    }
  }, [publishEvent, swimlaneState.cardHeight]);

  const onCardMount = useCallback((cardElement: HTMLElement) => {
    resizeObserverRef.current?.observe(cardElement, { box: 'border-box' });
  }, []);

  const onCardUnmount = useCallback((cardElement: HTMLElement) => {
    resizeObserverRef.current?.unobserve(cardElement);
  }, []);

  const getCellCardIDs = useCallback(
    (cellId: TCellId) => swimlaneState.cellCardIDs.get(cellId) ?? emptyCellCardIDs,
    [swimlaneState.cellCardIDs],
  );

  const getCardProps = useCallback(
    (cardId: TCardId) => swimlaneState.cardProps.get(cardId),
    [swimlaneState.cardProps],
  );

  const getCardHeight = useCallback(
    (cardId: TCardId) => swimlaneState.cardHeight.get(cardId) ?? defaultCardHeight,
    [swimlaneState.cardHeight, defaultCardHeight],
  );

  const getRowHeight = useCallback(
    (rowID: TRowID) => {
      return Math.max(swimlaneState.rowHeight.get(rowID) ?? 0, ROW_HEIGHT_MIN.value);
    },
    [swimlaneState.rowHeight],
  );

  const getCellHeight = useCallback(
    (rowID: TRowID, columnID: TColumnID) => {
      const rawCellHeight = swimlaneState.cellHeight.get(rowID)?.get(columnID);
      if (rawCellHeight === undefined) return 0;
      return rawCellHeight + CELL_WRAPPER_PADDING.value + CELL_WRAPPER_PADDING_BOTTOM.value;
    },
    [swimlaneState.cellHeight],
  );

  const hasCardDraft = useCallback(
    (draftId: TCardDraftId) => Boolean(swimlaneState.cardDraft.has(draftId)),
    [swimlaneState.cardDraft],
  );

  const addCardDraft = useCallback((draftId: TCardDraftId) => {
    setSwimlaneState((prevState) => {
      if (prevState.cardDraft.has(draftId)) {
        return prevState;
      }

      const nextState = { ...prevState, cardDraft: new Set(prevState.cardDraft) };
      nextState.cardDraft.add(draftId);
      const cellId = buildCellId(parseDraftId(draftId));

      return resetStateCellHeight(nextState, cellId);
    });
  }, []);

  const removeCardDraft = useCallback((draftId: TCardDraftId) => {
    setSwimlaneState((prevState) => {
      if (!prevState.cardDraft.has(draftId)) {
        return prevState;
      }

      const nextState = { ...prevState, cardDraft: new Set(prevState.cardDraft) };
      nextState.cardDraft.delete(draftId);
      const cellId = buildCellId(parseDraftId(draftId));

      return resetStateCellHeight(nextState, cellId);
    });
  }, []);

  const onCardDropIndicatorShown = useCallback((overCardId: TCardId, draggingCardId: TCardId) => {
    setSwimlaneState((prevState) => {
      if (prevState.cardsDraggedOver.get(overCardId) === draggingCardId) {
        return prevState;
      }
      const nextState = {
        ...prevState,
        cardsDraggedOver: new Map(prevState.cardsDraggedOver),
        rowHeight: new Map(prevState.rowHeight),
      };
      nextState.cardsDraggedOver.set(overCardId, draggingCardId);
      const { rowID } = parseCardId(overCardId);

      return resetStateRowHeight(nextState, rowID);
    });
  }, []);

  const onCardDropIndicatorHidden = useCallback((overCardId: TCardId) => {
    setSwimlaneState((prevState) => {
      if (!prevState.cardsDraggedOver.has(overCardId)) {
        return prevState;
      }
      const nextState = {
        ...prevState,
        cardsDraggedOver: new Map(prevState.cardsDraggedOver),
        rowHeight: new Map(prevState.rowHeight),
      };
      nextState.cardsDraggedOver.delete(overCardId);
      const { rowID } = parseCardId(overCardId);

      return resetStateRowHeight(nextState, rowID);
    });
  }, []);

  useCardsHeightCalculation({
    calculateCardHeightRef,
    onCardsHeightChange,
    unmeasuredCards: calculateCardHeight
      ? swimlaneState.unmeasuredCards
      : EMPTY_STATE.unmeasuredCards,
    cardProps: swimlaneState.cardProps,
  });

  return (
    <SwimlaneStateContext.Provider
      value={{
        getCellCardIDs,
        getCardProps,
        getCardHeight,
        getRowHeight,
        getCellHeight,
        hasCardDraft,
        addCardDraft,
        removeCardDraft,
        onCardMount,
        onCardUnmount,
        onCardDropIndicatorShown,
        onCardDropIndicatorHidden,
      }}
    >
      <CardHeightCalculationStateManager onStateChange={setCardHeightCalculatorState} />
      {children}
    </SwimlaneStateContext.Provider>
  );
};

export const useSwimlaneState = (): TSwimlaneStateContext => {
  const context = useContext(SwimlaneStateContext);
  if (!context) {
    throw new Error('useSwimlaneState must be used within a SwimlaneStateProvider');
  }
  return context;
};
